import React, { useState, useEffect } from "react";
import { Download, X, Smartphone } from "lucide-react";

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem("ff_install_dismissed");
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    // Android/Desktop Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show iOS prompt after short delay
    if (ios && !window.navigator.standalone) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") setIsInstalled(true);
      setDeferredPrompt(null);
    }
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem("ff_install_dismissed", Date.now().toString());
  };

  if (isInstalled || !showBanner) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
      style={{ background: "linear-gradient(to top, #090E33 0%, #090E33ee 100%)" }}
    >
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex items-center gap-4 p-4 bg-ff-surface border border-ff-border rounded-lg">
          <div className="w-12 h-12 rounded-xl bg-ff-bg flex items-center justify-center shrink-0 border border-ff-border">
            <Smartphone size={22} className="text-ff-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-ff-text text-sm font-medium">Get the Freddy Fiction app</p>
            {isIOS ? (
              <p className="text-ff-text-muted text-xs mt-0.5">
                Tap <span className="inline-flex items-center"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> then "Add to Home Screen"
              </p>
            ) : (
              <p className="text-ff-text-muted text-xs mt-0.5">Read and listen offline, right from your home screen</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isIOS && deferredPrompt && (
              <button
                onClick={handleInstall}
                data-testid="install-btn"
                className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5"
                style={{ background: "#FF4343", color: "#FFFFFF" }}
              >
                <Download size={14} /> Install
              </button>
            )}
            <button
              onClick={handleDismiss}
              data-testid="install-dismiss"
              className="w-8 h-8 flex items-center justify-center text-ff-text-muted hover:text-ff-text transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
